// store/slices/authSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// API Response Structure Interface (from your API)
export interface AdminFromAPI {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  roleId: number;
  isActive: boolean;
  lastLoginAt: string;
  createdAt: string;
  updatedAt: string;
  role: {
    id: number;
    name: string;
    permissions?: string[];
  };
}

// Redux User Interface (internal state)
export interface User {
  _id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  permissions: string[];
  isActive: boolean;
  profileImage: string | null;
  lastLogin: string;
  loginAttempts: number;
  lockUntil: string | null;
  twoFactorEnabled: boolean;
  department: string;
  employeeId: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface Tokens {
  accessToken: string;
  refreshToken: string;
}

interface AuthState {
  isLoggedIn: boolean;
  user: User | null;
  tokens: Tokens | null;
  role: string | null;
  rehydrated: boolean;
}

// Helper function to transform API admin data to Redux User interface
export const transformAdminToUser = (admin: AdminFromAPI): User => {
  return {
    _id: admin.id.toString(),
    name: `${admin.firstName} ${admin.lastName}`,
    email: admin.email,
    phone: admin.phone || "",
    role: admin.role.name, // "admin" or "superadmin"
    permissions: admin.role.permissions || [],
    isActive: admin.isActive,
    profileImage: null,
    lastLogin: admin.lastLoginAt,
    loginAttempts: 0,
    lockUntil: null,
    twoFactorEnabled: false,
    department: "",
    employeeId: admin.id.toString(),
    createdAt: admin.createdAt,
    updatedAt: admin.updatedAt,
    __v: 0
  };
};

const storedUser = localStorage.getItem("user");
const storedTokens = localStorage.getItem("tokens");
let parsedUser: User | null = null;
let parsedTokens: Tokens | null = null;

try {
  parsedUser = storedUser ? JSON.parse(storedUser) : null;
} catch {
  parsedUser = null;
}

try {
  parsedTokens = storedTokens ? JSON.parse(storedTokens) : null;
} catch {
  parsedTokens = null;
}

const initialState: AuthState = {
  isLoggedIn: !!parsedUser && !!parsedTokens,
  user: parsedUser,
  tokens: parsedTokens,
  role: parsedUser?.role || null,
  rehydrated: !!(parsedUser && parsedTokens),
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    login: (state, action: PayloadAction<{ user: User; tokens: Tokens }>) => {
      state.isLoggedIn = true;
      state.user = action.payload.user;
      state.tokens = action.payload.tokens;
      state.role = action.payload.user.role;
      state.rehydrated = true;
      localStorage.setItem("user", JSON.stringify(action.payload.user));
      localStorage.setItem("tokens", JSON.stringify(action.payload.tokens));
    },
    logout: (state) => {
      state.isLoggedIn = false;
      state.user = null;
      state.tokens = null;
      state.role = null;
      state.rehydrated = false;
      localStorage.removeItem("user");
      localStorage.removeItem("tokens");
    },
    setUser: (state, action: PayloadAction<{ user: User; tokens: Tokens } | null>) => {
      if (action.payload) {
        state.user = action.payload.user;
        state.tokens = action.payload.tokens;
        state.role = action.payload.user.role;
        state.isLoggedIn = true;
        state.rehydrated = true;
        localStorage.setItem("user", JSON.stringify(action.payload.user));
        localStorage.setItem("tokens", JSON.stringify(action.payload.tokens));
      } else {
        state.user = null;
        state.tokens = null;
        state.role = null;
        state.isLoggedIn = false;
        state.rehydrated = false;
        localStorage.removeItem("user");
        localStorage.removeItem("tokens");
      }
    },
    updateTokens: (state, action: PayloadAction<Tokens>) => {
      state.tokens = action.payload;
      localStorage.setItem("tokens", JSON.stringify(action.payload));
    },
    markAsRehydrated: (state) => {
      state.rehydrated = true;
    },
  },
});

export const { login, logout, setUser, updateTokens, markAsRehydrated } = authSlice.actions;
export default authSlice.reducer;
